import { Stack } from "@mui/material";
import React, { PureComponent } from "react";
import Dropdown from "../../core/Dropdown";
import { XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar, ResponsiveContainer } from "recharts";
import styles from "./WarehouseInfo.module.scss";
import { getWarehouseTrendsInterface } from "../../../apis/dashboard";
import { Option } from "../../../libs/types";
import { str2number } from "../../../libs/helpers";

interface WarehouseTrendsInterface {
  yashio: { year: number; month: number; total_quantity: string;}[];
  misato: { year: number; month: number; total_quantity: string;}[];
}

interface ChartDataType {
  month: string;
  quantity: number;
}

const getCurrentNendo = () => {
  const now = new Date();
  return now.getMonth() > 8 ? now.getFullYear() + 1 : now.getFullYear();
}

const createYearOptions = () => {
  const choices: Option[] = [];
  const minYear = 2022;
  const maxYear = getCurrentNendo();
  for (let i = maxYear; i >= minYear; i--) {
    choices.push({ name: String(i), value: i});
  }

  return choices;
};

const CustomTooltip = (props: {active?: any; payload?: any; label?: string; year: number; category: number; warehouse: string; total: number}) => {
  if (props.active && props.payload && props.payload.length) {
    const percent = props.total > 0 ? Math.round(props.payload[0].value / props.total * 100) : 0
    const postfix = props.category === 0 ? "坪" : "個"
    const yearLabel = props.label?.startsWith('0') ? props.year : props.year - 1
    return (
      <div style={
        {
          border: "1px solid #CCCCCC",
          backgroundColor: "white",
          borderRadius: "3px",
          fontSize: "12px",
          color: "#333333",
          padding: "5px"
        }
      }>
        <div>{`${yearLabel}年${props.label}`}</div>
        <div>{`${props.warehouse} : ${props.payload[0].value}${postfix} (${percent}%)`}</div>
      </div>
    );
  }

  return null;
};

class CustomizedAxisTick extends PureComponent<{x?: any, y?:any, payload?: any, index?: number, year: number}> {
  render() {
    const { x, y, payload } = this.props;

    return (
      <g transform={`translate(${x},${y})`}>
        <text x={-10} y={8} dy={0} fill="#666" fontSize={12}>
          {payload.value}
        </text>
        {this.props.index === 0 &&
          <text x={-14} y={23} dy={0} fill="#666" fontSize={11}>
            {this.props.year - 1}年
          </text>
        }
        {this.props.index === 11 &&
          <text x={-15} y={23} dy={0} fill="#666" fontSize={11}>
            {this.props.year}年
          </text>
        }
      </g>
    );
  }
}

const X_AXIS = [10, 11, 12, 1, 2, 3, 4, 5, 6, 7, 8, 9];
const YASHIO_TSUBO_TOTAL = 1050;
const MISATO_TSUBO_TOTAL = 800;

const WarehouseInfoComponent: React.FC = () => {
  const [category, setCategory] = React.useState<number>(0);
  const [year, setYear] = React.useState<number>(getCurrentNendo());
  const [apiData, setApiData] = React.useState<WarehouseTrendsInterface>();

  React.useEffect(() => {
    let isActive = true;
    getWarehouseTrendsInterface(0, getCurrentNendo())
      .then((res) => {
        if (isActive) {
          setApiData(res.data.data)
        }
      })
      .catch((err) => console.log(err));

    return () => {
      isActive = false;
    }
  }, []);

  const chartData = React.useMemo(() => {
    const yashioData = X_AXIS.map(month => {
      const yashio = apiData?.yashio.find(data => data.month === month)
      return {
        month: String(month).padStart(2, '0') + "月",
        quantity: yashio ? str2number(yashio.total_quantity) : 0,
      } as ChartDataType
    });
    const misatoData = X_AXIS.map(month => {
      const misato = apiData?.misato.find(data => data.month === month)
      return {
        month: String(month).padStart(2, '0') + "月",
        quantity: misato ? str2number(misato.total_quantity) : 0,
      } as ChartDataType
    });
    
    const yashioTotal = apiData?.yashio.reduce((prev, data) => prev + str2number(data.total_quantity), 0) ?? 0;
    const misatoTotal = apiData?.misato.reduce((prev, data) => prev + str2number(data.total_quantity), 0) ?? 0;

    return {yashioData, misatoData, yashioTotal, misatoTotal}
  }, [apiData]);

  async function onChangeNendo (value: number) {
    setYear(value)
    const response = await getWarehouseTrendsInterface(category, value)
    setApiData(response.data.data)
  }

  async function onChangeCategory (value: number) {
    setCategory(value)
    const response = await getWarehouseTrendsInterface(value, year)
    setApiData(response.data.data)
  }

  return (
    <div className={styles.WarehouseChartContainer}>
      <div className={styles.WarehouseChartContent}>
        <div className={styles.title}>
          <h2>倉庫別情報</h2>
        </div>
        <div className={styles.body}>
          <Stack spacing={2} style={{width: "130px"}}>
            <Dropdown
              options={[
                { name: "坪数", value: 0 },
                { name: "入荷数", value: 1 },
                { name: "出庫数", value: 2 },
              ]}
              value={category}
              label="カテゴリー"
              onChange={(val) => onChangeCategory(val)}
            />
            <Dropdown
              options={createYearOptions()}
              value={year}
              label="年度"
              onChange={(val) => onChangeNendo(val)}
            />
          </Stack>
          <ResponsiveContainer width={"44%"} height={300}>
            <BarChart
              style={{ flexGrow: "1" }}
              data={chartData.yashioData}
              margin={{
                top: 15,
                right: 0,
                left: 0,
                bottom: 20,
              }}
            >
              <CartesianGrid stroke="#ccc" />
              <XAxis minTickGap={0} fontSize={12} dataKey="month" tick={<CustomizedAxisTick year={year} />} />
              <YAxis fontSize={12} dy={-5} />
              <Tooltip content={
                <CustomTooltip year={year} category={category} warehouse="ヤシオ" total={category === 0 ? YASHIO_TSUBO_TOTAL : chartData.yashioTotal} />
              }/>
              <Bar barSize={25} type="monotone" dataKey="quantity" fill="#75A857" />
              <text x="300" y="283" dominantBaseline="hanging" fontSize="15">
                ヤシオ
              </text>
            </BarChart>
          </ResponsiveContainer>
          <ResponsiveContainer width={"44%"} height={300}>
            <BarChart
              style={{ flexGrow: "1" }}
              data={chartData.misatoData}
              margin={{
                top: 15,
                right: 0,
                left: 0,
                bottom: 20,
              }}
            >
              <CartesianGrid stroke="#ccc" />
              <XAxis minTickGap={0} fontSize={12} dataKey="month" tick={<CustomizedAxisTick year={year} />} />
              <YAxis fontSize={12} dy={-5} dataKey="quantity" />
              <Tooltip content={
                <CustomTooltip year={year} category={category} warehouse="ミサト" total={category === 0 ? MISATO_TSUBO_TOTAL : chartData.misatoTotal} />
              }/>
              <Bar barSize={25} type="monotone" dataKey="quantity" fill="#1E53A3" />
              <text x="300" y="283" dominantBaseline="hanging" fontSize="15">
                ミサト
              </text>
            </BarChart>
          </ResponsiveContainer>

        </div>
      </div>
    </div>
  );
};

export default WarehouseInfoComponent;
